import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { I18nProvider } from "./lib/i18n";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { trackPage, trackPageviewServer, trackPageExit, installGlobalClickTracker } from "./lib/analytics";
import { captureAttribution } from "./lib/attribution";
import { Layout } from "./components/Layout";
import { MobileAppBanner } from "./components/MobileAppBanner";
import { LiveToast } from "./components/social/LiveToast";
import { ExitIntentPopup } from "./components/social/ExitIntentPopup";
import AIChatLauncher from "./components/ai/AIChatLauncher";
import Home from "./pages/Home";
import Woningen from "./pages/Woningen";
import WoningDetail from "./pages/WoningDetail";
import ScamCheck from "./pages/ScamCheck";
import Prijzen from "./pages/Prijzen";
import Dashboard from "./pages/Dashboard";
import Welkom from "./pages/Welkom";
import Profiel from "./pages/Profiel";
import Vault from "./pages/Vault";
import Alerts from "./pages/Alerts";
import Reacties from "./pages/Reacties";
import Bezichtigingen from "./pages/Bezichtigingen";
import Referrals from "./pages/Referrals";
import Instellingen from "./pages/Instellingen";
import MakelaarDashboard from "./pages/makelaar/MakelaarDashboard";
import Kandidaten from "./pages/makelaar/Kandidaten";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import BillingSuccess from "./pages/BillingSuccess";
import LegalPage from "./pages/legal/LegalPage";
import OverOns from "./pages/OverOns";
import Admin from "./pages/Admin";
import ListingSeo from "./pages/seo/ListingSeo";
import CategorySeo from "./pages/seo/CategorySeo";
import Koopwoningen from "./pages/koop/Koopwoningen";
import KoopDetail from "./pages/koop/KoopDetail";
import KoopMakelaar from "./pages/koop/KoopMakelaar";
import KoopWizard from "./pages/koop/KoopWizard";
import Waardebepaling from "./pages/koop/Waardebepaling";
import WoningwaardePage from "./pages/woningwaarde/WoningwaardePage";
import PremiumHub from "./pages/PremiumHub";
import Reactiecoach from "./pages/tools/Reactiecoach";
import FotoAnalyse from "./pages/tools/FotoAnalyse";
import AdminMarketing from "./pages/AdminMarketing";
import AdminBehavior from "./pages/AdminBehavior";
import AdminLeads from "./pages/AdminLeads";
import AdminLeadDetail from "./pages/AdminLeadDetail";
import AdminPartners from "./pages/AdminPartners";
import AdminPartnerDetail from "./pages/AdminPartnerDetail";
import AdminMortgage from "./pages/AdminMortgage";
import Vastgoeddiensten from "./pages/Vastgoeddiensten";
import AdminUsers, { AdminUserDetail } from "./pages/AdminUsers";
import AdminBlog from "./pages/AdminBlog";
import AdminBlogEdit from "./pages/AdminBlogEdit";
import AdminBrokerListings from "./pages/AdminBrokerListings";
import AdminListingEdit from "./pages/AdminListingEdit";
import AdminListingPreview from "./pages/AdminListingPreview";
import AdminMakelaarApprovals from "./pages/AdminMakelaarApprovals";
import AdminMakelaarFeeds from "./pages/AdminMakelaarFeeds";
import AdminAnalyticsDiagnostics from "./pages/AdminAnalyticsDiagnostics";
import MakelaarPending from "./pages/makelaar/MakelaarPending";
import HuurWizard from "./pages/makelaar/HuurWizard";
import VerkoopDashboard from "./pages/particulier/VerkoopDashboard";
import VerkoopWizard from "./pages/particulier/VerkoopWizard";
import VerkoopSubmitted from "./pages/particulier/VerkoopSubmitted";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";

const ProtectedRoute = ({ children }) => {  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="animate-spin text-brandblue" size={32} />
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="animate-spin text-brandblue" size={32} />
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  if (user.role !== "admin") {
    return <Navigate to="/" replace />;
  }
  return children;
};

const AnalyticsListener = () => {
  const location = useLocation();
  const prevRef = React.useRef({ path: null, ts: 0 });
  // location.pathname only — filter/query changes on the same page MUST NOT
  // generate new pageviews. This dependency keeps a single navigation to one
  // pageview even when search params change (e.g. filtering /woningen).
  const pathname = location.pathname;
  useEffect(() => {
    // Admin / internal paths are not public analytics.
    if (pathname.startsWith("/admin")) {
      prevRef.current = { path: pathname, ts: Date.now() };
      return;
    }
    const now = Date.now();
    const prev = prevRef.current;
    // Emit exit for previous page (if any) with dwell time.
    if (prev.path && !prev.path.startsWith("/admin")) {
      trackPageExit(prev.path, now - prev.ts);
    }
    trackPage(pathname + location.search);
    trackPageviewServer(pathname);
    prevRef.current = { path: pathname, ts: now };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Final exit event on tab close / hide (uses sendBeacon under the hood).
  useEffect(() => {
    const onHide = () => {
      const p = prevRef.current;
      if (p.path && !p.path.startsWith("/admin")) {
        trackPageExit(p.path, Date.now() - p.ts);
      }
    };
    window.addEventListener("pagehide", onHide);
    document.addEventListener(
      "visibilitychange",
      () => {
        if (document.visibilityState === "hidden") onHide();
      }
    );
    return () => window.removeEventListener("pagehide", onHide);
  }, []);

  return null;
};

function App() {
  useEffect(() => {
    captureAttribution();
    installGlobalClickTracker();
  }, []);
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <AnalyticsListener />
          <MobileAppBanner />
          <AIChatLauncher />
          <LiveToast />
          <ExitIntentPopup />
          <Routes>
            <Route element={<Layout />}>
              <Route path="/" element={<Home />} />
              <Route path="/woningen" element={<Woningen />} />
              <Route path="/woning/:id" element={<WoningDetail />} />
              {/* Koop (for-sale) — consumer */}
              <Route path="/koopwoningen" element={<Koopwoningen variant="default" />} />
              <Route path="/koopwoningen/:stad" element={<Koopwoningen variant="city" />} />
              <Route path="/koopwoning/:stad/:slug" element={<KoopDetail />} />
              <Route path="/huis-kopen/:stad" element={<Koopwoningen variant="type" lockedType="huis" />} />
              <Route path="/appartement-kopen/:stad" element={<Koopwoningen variant="type" lockedType="appartement" />} />
              <Route path="/waardebepaling" element={<Waardebepaling />} />
              <Route path="/woningwaarde" element={<WoningwaardePage />} />
              <Route path="/woningwaarde/:stad" element={<WoningwaardePage />} />
              {/* Koop — makelaar */}
              <Route path="/koop/makelaar" element={<ProtectedRoute><KoopMakelaar /></ProtectedRoute>} />
              <Route path="/koop/makelaar/wizard" element={<ProtectedRoute><KoopWizard /></ProtectedRoute>} />
              <Route path="/koop/makelaar/wizard/:lid" element={<ProtectedRoute><KoopWizard /></ProtectedRoute>} />
              <Route path="/scam-check" element={<ScamCheck />} />
              <Route path="/premium" element={<PremiumHub />} />
              <Route path="/reactiecoach" element={<ProtectedRoute><Reactiecoach /></ProtectedRoute>} />
              <Route path="/foto-analyse" element={<ProtectedRoute><FotoAnalyse /></ProtectedRoute>} />
              <Route path="/prijzen" element={<Prijzen />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/wachtwoord-vergeten" element={<ForgotPassword />} />
              <Route path="/wachtwoord-reset" element={<ResetPassword />} />
              <Route path="/billing/success" element={<BillingSuccess />} />
              <Route path="/privacy" element={<LegalPage type="privacy" />} />
              <Route path="/voorwaarden" element={<LegalPage type="voorwaarden" />} />
              <Route path="/cookiebeleid" element={<LegalPage type="cookiebeleid" />} />
              <Route path="/over-ons" element={<OverOns />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route path="/profiel" element={<ProtectedRoute><Profiel /></ProtectedRoute>} />
              <Route path="/welkom" element={<ProtectedRoute><Welkom /></ProtectedRoute>} />
              <Route path="/vault" element={<ProtectedRoute><Vault /></ProtectedRoute>} />
              <Route path="/alerts" element={<ProtectedRoute><Alerts /></ProtectedRoute>} />
              <Route path="/reacties" element={<ProtectedRoute><Reacties /></ProtectedRoute>} />
              <Route path="/bezichtigingen" element={<ProtectedRoute><Bezichtigingen /></ProtectedRoute>} />
              <Route path="/referrals" element={<ProtectedRoute><Referrals /></ProtectedRoute>} />
              <Route path="/instellingen" element={<ProtectedRoute><Instellingen /></ProtectedRoute>} />
              <Route path="/makelaar" element={<ProtectedRoute><MakelaarDashboard /></ProtectedRoute>} />
              <Route path="/makelaar/wacht-op-goedkeuring" element={<ProtectedRoute><MakelaarPending /></ProtectedRoute>} />
              <Route path="/makelaar/woning/nieuw" element={<ProtectedRoute><HuurWizard /></ProtectedRoute>} />
              <Route path="/makelaar/woning/bewerken/:lid" element={<ProtectedRoute><HuurWizard /></ProtectedRoute>} />
              <Route path="/makelaar/woning/:lid/kandidaten" element={<ProtectedRoute><Kandidaten /></ProtectedRoute>} />
              <Route path="/mijn-woning-verkopen" element={<ProtectedRoute><VerkoopDashboard /></ProtectedRoute>} />
              <Route path="/mijn-woning-verkopen/nieuw" element={<ProtectedRoute><VerkoopWizard /></ProtectedRoute>} />
              <Route path="/mijn-woning-verkopen/bewerken/:lid" element={<ProtectedRoute><VerkoopWizard /></ProtectedRoute>} />
              <Route path="/mijn-woning-verkopen/ingediend/:lid" element={<ProtectedRoute><VerkoopSubmitted /></ProtectedRoute>} />
              <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
              <Route path="/admin/marketing" element={<AdminRoute><AdminMarketing /></AdminRoute>} />
              <Route path="/admin/behavior" element={<AdminRoute><AdminBehavior /></AdminRoute>} />
              <Route path="/admin/vastgoed-leads" element={<AdminRoute><AdminLeads /></AdminRoute>} />
              <Route path="/admin/vastgoed-leads/:id" element={<AdminRoute><AdminLeadDetail /></AdminRoute>} />
              <Route path="/admin/partners" element={<AdminRoute><AdminPartners /></AdminRoute>} />
              <Route path="/admin/partners/:id" element={<AdminRoute><AdminPartnerDetail /></AdminRoute>} />
              <Route path="/admin/mortgage" element={<AdminRoute><AdminMortgage /></AdminRoute>} />
              <Route path="/vastgoeddiensten" element={<Vastgoeddiensten />} />
              <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
              <Route path="/admin/users/:uid" element={<AdminRoute><AdminUserDetail /></AdminRoute>} />
              <Route path="/admin/broker-listings" element={<AdminRoute><AdminBrokerListings /></AdminRoute>} />
              <Route path="/admin/broker-listings/:kind/:lid" element={<AdminRoute><AdminListingEdit /></AdminRoute>} />
              <Route path="/admin/preview/:kind/:lid" element={<AdminRoute><AdminListingPreview /></AdminRoute>} />
              <Route path="/admin/makelaar-approvals" element={<AdminRoute><AdminMakelaarApprovals /></AdminRoute>} />
              <Route path="/admin/makelaar-feeds" element={<AdminRoute><AdminMakelaarFeeds /></AdminRoute>} />
              <Route path="/admin/analytics-diagnostics" element={<AdminRoute><AdminAnalyticsDiagnostics /></AdminRoute>} />
              <Route path="/admin/blog" element={<AdminRoute><AdminBlog /></AdminRoute>} />
              <Route path="/admin/blog/new" element={<AdminRoute><AdminBlogEdit /></AdminRoute>} />
              <Route path="/admin/blog/:id" element={<AdminRoute><AdminBlogEdit /></AdminRoute>} />

              {/* Public blog */}
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />

              {/* Public SEO pages (backed by the seo_listings index via /api/seo) */}
              <Route path="/huurwoning/:stad/:slug" element={<ListingSeo />} />
              <Route path="/huurwoningen/:stad" element={<CategorySeo variant="city" />} />
              <Route path="/appartement-huren/:stad" element={<CategorySeo variant="appartement" />} />
              <Route path="/studio-huren/:stad" element={<CategorySeo variant="studio" />} />
              <Route path="/kamer-huren/:stad" element={<CategorySeo variant="kamer" />} />
              <Route path="/huis-huren/:stad" element={<CategorySeo variant="huis" />} />
              {/* Price category keeps the exact URL shape /huurwoningen-onder-1500/{stad};
                  RRv7 needs a full-segment param (:priceseg) which the page guards by regex. */}
              <Route path="/:priceseg/:stad" element={<CategorySeo variant="price" />} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
